// Frittstående «Utvikling til Strid» (UtS/HPBP) — planprosessen på egen skjerm.
//
// HVORFOR DENNE FILEN FINNES:
// Panelet er 520 px bredt og nesten full høyde — bredest av alle panelene i COP-en.
// Som overlegg spiser det kartkolonnen. Stabsarbeid med planprosessen skjer i tillegg
// ofte parallelt med at en annen ser på kartet, så dette er panelet som mest av alle
// hører hjemme på en egen skjerm. Uten denne mounten fikk panelet aldri den
// standardiserte ⤢-knappen, fordi WindowManager bare gir den til paneler som har et
// ekte frittstående mål (`POPOUT_KEY_BY_CLASS`).
//
// HVA SOM ÆRLIG NOK IKKE VIRKER HER:
// To av hooksene i hovedvinduet leser fra kartet: VTSSN henter vær for KARTSENTERET,
// og KAVOD-faktorene «observasjon og skuddfelt» / «dekning og skjul» slår på
// dekningslaget og terrengskyggen. Det finnes ikke noe kart i dette vinduet. Framfor
// å slå av knappene — som ville sett ut som at funksjonen ikke finnes — sender vi
// forespørselen over SyncBus til hovedvinduet, som eier kartet, og forteller
// operatøren at svaret kommer der. Å late som beregningen skjedde her ville produsert
// underlag ingen har regnet på.
import { PlanprosessPanel } from '../ui/planprosess.js';
import type { SyncBus } from '../sync.js';

/** Meldingstyper hovedvinduet lytter på for å svare et poppet-ut planpanel. */
export const PLAN_SYNC = {
  /** Ber hovedvinduet hente vær for kartsenteret og kringkaste svaret. */
  bedOmVaer: 'plan:vaer?',
  /** Svar med værserien (eller null hvis MET ikke svarte). */
  vaerSvar: 'plan:vaer',
  /** Ber hovedvinduet om kartsenteret (til sol-/tussmørkeberegning). */
  bedOmSenter: 'plan:senter?',
  /** Svar med kartsenteret. Kringkastes også når kartet flyttes. */
  senterSvar: 'plan:senter',
  /** Ber hovedvinduet slå på terrengverktøyet for en KAVOD-faktor. */
  bedOmLende: 'plan:lende?',
  /** Ber hovedvinduet åpne ordrepanelet. */
  apneOrdre: 'plan:ordre',
} as const;

/** Hvor lenge vi venter på at hovedvinduet svarer på en værforespørsel. */
export const VAER_TIDSAVBRUDD_MS = 8000;

export function mountPlanprosessPanel(root: HTMLElement, sync: SyncBus): void {
  document.body.classList.add('panel-standalone');
  root.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.className = 'standalone-panel';
  const head = document.createElement('div');
  head.className = 'standalone-head';
  head.append(spenn('brand', 'SKADE'), spenn('standalone-title', 'Utvikling til Strid'));

  // Varsellinje: i eget vindu finnes ingen showAlarm() fra skallet.
  const varselEl = document.createElement('div');
  varselEl.className = 'standalone-varsel';
  varselEl.setAttribute('role', 'status');
  varselEl.setAttribute('aria-live', 'polite');
  varselEl.hidden = true;
  let varselTimer: ReturnType<typeof setTimeout> | undefined;
  const varsle = (t: string) => {
    varselEl.textContent = t;
    varselEl.hidden = false;
    clearTimeout(varselTimer);
    varselTimer = setTimeout(() => { varselEl.hidden = true; }, 7000);
  };

  /**
   * Siste kjente kartsenter fra hovedvinduet.
   *
   * HVORFOR EN CACHE OG IKKE ET SPØRSMÅL PER KALL: `hentPosisjon` er SYNKRON,
   * fordi soltidene regnes under tegning av panelet. Et rundturspørsmål der
   * ville krevd at hele blokka ble asynkron, og da ville tidene blinket inn
   * etter at resten var malt. Vi ber om senteret ved oppstart, hovedvinduet
   * kringkaster det også når kartet flyttes, og `null` til det første svaret
   * kommer er en ÆRLIG tilstand som sol-blokka sier fra om.
   */
  let sisteSenter: { lat: number; lon: number } | null = null;
  sync.on(PLAN_SYNC.senterSvar, (m) => {
    const s = (m as { senter?: { lat?: unknown; lon?: unknown } }).senter;
    if (!s || typeof s.lat !== 'number' || typeof s.lon !== 'number') return;
    sisteSenter = { lat: s.lat, lon: s.lon };
    panel.oppdater();
  });
  sync.post({ type: PLAN_SYNC.bedOmSenter });

  const panel = new PlanprosessPanel({
    varsle,
    hentPosisjon: () => sisteSenter,
    hentVaer: () => hentVaerViaHovedvindu(sync, varsle),
    hentLende: async (faktor) => {
      sync.post({ type: PLAN_SYNC.bedOmLende, faktor });
      // Ærlig retur: verktøyet slås på i HOVEDVINDUET, og svaret leses av
      // operatøren der. Vi later ikke som vi har et tall.
      return `Terrengverktøyet er slått på i hovedvinduet — les av «${faktor}» der og skriv inn vurderingen.`;
    },
    apneOrdre: () => sync.post({ type: PLAN_SYNC.apneOrdre }),
  });

  // Frittstående: panelet er hele vinduet, så det skal alltid være åpent, og
  // `×`/Escape hører ikke hjemme her (man lukker nettleservinduet i stedet).
  panel.el.classList.add('open', 'embedded');
  panel.el.querySelector('.report-x')?.remove();

  wrap.append(head, varselEl, panel.el);
  root.append(wrap);
}

/**
 * Be hovedvinduet om vær for kartsenteret. Løser til `null` ved tidsavbrudd —
 * ikke til en tom serie, for tomt vær og «vi fikk ikke svar» er ikke det samme,
 * og VTSSN-feltene skal ikke fylles med stillhet.
 */
export function hentVaerViaHovedvindu(
  sync: SyncBus,
  varsle: (t: string) => void,
  tidsavbruddMs = VAER_TIDSAVBRUDD_MS,
): Promise<Record<string, unknown> | null> {
  return new Promise((løs) => {
    let ferdig = false;
    const av = sync.on(PLAN_SYNC.vaerSvar, (m) => {
      if (ferdig) return;
      ferdig = true; av(); clearTimeout(t);
      const serie = (m as { serie?: Record<string, unknown> | null }).serie;
      løs(serie ?? null);
    });
    const t = setTimeout(() => {
      if (ferdig) return;
      ferdig = true; av();
      varsle('Fikk ikke vær fra hovedvinduet — er COP-en åpen? VTSSN må fylles manuelt.');
      løs(null);
    }, tidsavbruddMs);
    sync.post({ type: PLAN_SYNC.bedOmVaer });
  });
}

function spenn(cls: string, tekst: string): HTMLElement {
  const e = document.createElement('span');
  e.className = cls;
  e.textContent = tekst;
  return e;
}
